/**
 * @file invoiceDetailReport.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description Reports → Invoice Detail: the columns, groupings and totals
 *              modes, how a cell, a group heading and a totals row read, and
 *              the export's table — ONE copy for the screen and the server.
 *
 * Cam, 2 Nov 2026: the totals and the export are the whole filtered result's,
 * not the page on screen's. The server (routes/reports.js) pages by line and
 * gives every group on the page its WHOLE group's subtotal, whether the group
 * started on an earlier page (`continued`) and whether its last line is on
 * this page (`ends`) — the subtotal shows only there — and the grand total
 * over every page. The export is built on the server from every line (or the
 * page, if chosen), through exportTable below, so the PDF prints each figure
 * exactly as the screen shows it.
 *
 * The server runs a CommonJS copy of this file (utils/invoiceDetailReport.js)
 * written by `node scripts/sync-shared-utils.js` — edit here, then re-run that.
 * Keep it to single-line named imports and `export function` / `export const`.
 */

import { formatCurrency, formatPrice } from './currencyFormatter';
import { formatQuantity } from './numberFormatters';

// All columns the user can show/hide. `key` matches a row property from the
// server. `default` flags the default set (leads with INV# and embeds the item
// code as a chip under the description rather than its own column).
export const INVOICE_DETAIL_COLUMNS = [
  { key: 'invoice_no', label: 'INV#', default: true, align: 'left' },
  { key: 'invoice_date', label: 'Date', default: true, align: 'left' },
  { key: 'source', label: 'Source', default: true, align: 'left' },
  { key: 'reference', label: 'Ref', default: true, align: 'left' },
  { key: 'description', label: 'Product', default: true, align: 'left' },
  { key: 'quantity', label: 'Qty', default: true, align: 'right', isNumber: true, totals: true },
  // isPrice: a unit price shows 2-to-4dp (formatPrice); the other currency
  // columns are settled money and stay at 2dp.
  { key: 'unit_price', label: 'Unit (ex)', default: true, align: 'right', isCurrency: true, isPrice: true },
  { key: 'discount_amount', label: 'Discount', default: true, align: 'right', isCurrency: true, totals: true, zeroAsDash: true },
  { key: 'tax_amount', label: 'VAT', default: true, align: 'right', isCurrency: true, totals: true, zeroAsDash: true },
  { key: 'line_total', label: 'Gross', default: true, align: 'right', isCurrency: true, totals: true },
  { key: 'invoice_total', label: 'Inv Total', default: true, align: 'right', isCurrency: true },
  { key: 'status', label: 'Status', default: true, align: 'left' },
  // Additional columns the user can opt-in
  { key: 'contact', label: 'Contact', default: false, align: 'left' },
  { key: 'item_code', label: 'Item #', default: false, align: 'left' },
  { key: 'due_date', label: 'Due', default: false, align: 'left' },
  { key: 'tax_type', label: 'Tax Type', default: false, align: 'left' },
  { key: 'account_code', label: 'Account', default: false, align: 'left' },
  { key: 'account_rep', label: 'Rep', default: false, align: 'left' }
];

/** The figures the report totals — every group's subtotal and the grand total. */
export const INVOICE_DETAIL_TOTAL_KEYS = ['quantity', 'discount_amount', 'tax_amount', 'line_total'];

// Grouping dimensions — match Xero's Invoice Detail options.
export const INVOICE_DETAIL_GROUPINGS = [
  { value: 'contact', label: 'Contact' },
  { value: 'invoice_no', label: 'Invoice No.' },
  { value: 'item_code', label: 'Product Code' },
  { value: 'account_code', label: 'Sales Account Code' },
  { value: 'status', label: 'Status' }
];

export const INVOICE_DETAIL_TOTALS_MODES = [
  { value: 'per-group', label: 'Per Group + Bottom' },
  { value: 'bottom', label: 'Bottom Only' },
  { value: 'per-group-only', label: 'Per Group Only' },
  { value: 'none', label: 'None' }
];

/** The grouping asked for, else Contact. */
export function groupingOf(groupBy) {
  return INVOICE_DETAIL_GROUPINGS.some(g => g.value === groupBy) ? groupBy : 'contact';
}

/** The totals mode asked for, else Per Group + Bottom. */
export function totalsModeOf(mode) {
  return INVOICE_DETAIL_TOTALS_MODES.some(m => m.value === mode) ? mode : 'per-group';
}

export function showsGroupTotals(mode) {
  const m = totalsModeOf(mode);
  return m === 'per-group' || m === 'per-group-only';
}

export function showsBottomTotals(mode) {
  const m = totalsModeOf(mode);
  return m === 'per-group' || m === 'bottom';
}

/** The columns with these keys, in the report's own order. */
export function columnsFor(keys) {
  const wanted = new Set((keys || []).map(String));
  return INVOICE_DETAIL_COLUMNS.filter(c => wanted.has(c.key));
}

/** A column that carries a figure in the totals rows. */
export function isTotalColumn(col) {
  return !!(col && col.totals);
}

const plural = (n, word) => `${Number(n || 0).toLocaleString('en-GB')} ${word}${Number(n) === 1 ? '' : 's'}`;

/** "12 lines", "1 line". */
export function linesText(n) {
  return plural(n, 'line');
}

/**
 * Compact DD/MM/YY — narrower than the locale-formatted version so the Date
 * column doesn't bloat the table. A plain 'YYYY-MM-DD' is read as that day
 * wherever the code runs; anything else as a moment in local time.
 */
export function formatDateCompact(val) {
  if (!val) return '';
  const plain = typeof val === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(val) : null;
  if (plain) return `${plain[3]}/${plain[2]}/${plain[1].slice(-2)}`;
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

/**
 * One cell, as text (the export's cell; the screen draws links and pills over
 * the same values). The item code rides under the description when the Item #
 * column is not shown on its own.
 */
export function cellText(col, row, { showItemCodeColumn = false } = {}) {
  const val = row ? row[col.key] : null;
  if (col.key === 'invoice_date' || col.key === 'due_date') return val ? formatDateCompact(val) : '';
  if (col.key === 'contact') return [row.account_no, row.account_name].filter(Boolean).join(' · ');
  if (col.key === 'source') return (val || '').replace(/_/g, ' ');
  if (col.key === 'description') {
    const showInlineCode = row.item_code && !showItemCodeColumn;
    return showInlineCode ? `${val || ''} · ${row.item_code}` : (val == null ? '' : String(val));
  }
  if (col.zeroAsDash && (val == null || parseFloat(val) === 0)) return '—';
  if (col.isPrice) return val != null && val !== '' ? formatPrice(val) : '';
  if (col.isCurrency) return val != null && val !== '' ? formatCurrency(val) : '';
  if (col.isNumber) return formatQuantity(val);
  return val == null ? '' : String(val);
}

/** A total's figure as text — '—' for a zero in a column that dashes zeros. */
export function totalText(col, value) {
  const v = Number(value) || 0;
  if (col.zeroAsDash && v === 0) return '—';
  return col.isCurrency ? formatCurrency(v) : formatQuantity(v);
}

/** A totals row keyed by column, for the columns that carry a total. */
export function totalsRowText(totals, columns, keys = null) {
  const allowed = keys ? new Set(keys) : null;
  const out = {};
  for (const col of columns || []) {
    if (!isTotalColumn(col)) continue;
    if (allowed && !allowed.has(col.key)) continue;
    out[col.key] = totalText(col, totals ? totals[col.key] : 0);
  }
  return out;
}

/** The heading fields of the group a row belongs to. */
export function groupFromRow(row, key) {
  return {
    key,
    accountId: row.account_id,
    accountName: row.account_name,
    accountNo: row.account_no,
    parentAccountId: row.parent_account_id,
    parentAccountName: row.parent_account_name,
    parentAccountNo: row.parent_account_no,
    invoiceId: row.invoice_id,
    invoiceNo: row.invoice_no,
    invoiceDate: row.invoice_date,
    itemCode: row.item_code,
    description: row.description,
    accountCode: row.account_code,
    status: row.status
  };
}

/**
 * The page's rows as groups, in the server's order. `meta` is the server's
 * list for the page — each group's WHOLE line count and totals, `continued`
 * (it started on an earlier page) and `ends` (its last line is here). A row
 * with no group_key (an old server) makes its own group from the row.
 */
export function groupRows(rows, meta = []) {
  const byKey = new Map((meta || []).map(m => [m.key, m]));
  const order = [];
  const groups = new Map();
  for (const row of rows || []) {
    const key = row.group_key || `row-${row.line_id}`;
    if (!groups.has(key)) {
      const m = byKey.get(key) || {};
      groups.set(key, {
        ...groupFromRow(row, key),
        rows: [],
        lineCount: m.line_count != null ? Number(m.line_count) : null,
        totals: m.totals || null,
        continued: !!m.continued,
        ends: m.ends !== false
      });
      order.push(key);
    }
    groups.get(key).rows.push(row);
  }
  return order.map(k => {
    const g = groups.get(k);
    if (g.lineCount == null) g.lineCount = g.rows.length;
    return g;
  });
}

/** Subtotal caption for one group — the screen and the export alike. */
export function subtotalLabel(group, groupBy) {
  switch (groupingOf(groupBy)) {
    case 'invoice_no': return `Subtotal — ${group.invoiceNo || '(no invoice)'}`;
    case 'item_code': return `Subtotal — ${group.itemCode || group.description || '(no code)'}`;
    case 'account_code': return `Subtotal — ${group.accountCode || '(no account)'}`;
    case 'status': return `Subtotal — ${group.status || '(no status)'}`;
    case 'contact':
    default: {
      const familyName = group.parentAccountName || group.accountName;
      return `Subtotal — ${familyName}${group.parentAccountId ? ` (${group.accountName})` : ''}`;
    }
  }
}

/** "Total — all 3,653 lines": the grand total is every line on the filters. */
export function grandTotalLabel(total) {
  return `Total — all ${linesText(total)}`;
}

/** A group's heading as the export template's three slots. */
export function groupHead(group, groupBy) {
  switch (groupingOf(groupBy)) {
    case 'invoice_no':
      return {
        label: [group.invoiceNo, formatDateCompact(group.invoiceDate), group.accountName].filter(Boolean).join(' · ')
      };
    case 'item_code':
      return { badge: group.itemCode || '', label: group.description || (group.itemCode ? '' : '(no code)') };
    case 'account_code':
      return { label: group.accountCode || '—' };
    case 'status':
      return { label: group.status || '—' };
    case 'contact':
    default:
      return {
        prefix: group.parentAccountId
          ? [group.parentAccountName, group.parentAccountNo].filter(Boolean).join(' ')
          : '',
        label: group.accountName || '',
        badge: group.accountNo || ''
      };
  }
}

/**
 * The export's table: columns, groups (heading, rows as text, subtotal) and
 * the grand total, from the server's rows, group list and totals. `totalKeys`
 * are the totals still wanted (the export window can switch some off; a
 * column left out has none). A group cut by the page (a one-page export) is
 * headed "(continued)" and has its subtotal only where it ends — the whole
 * group's, never the part on the page.
 */
export function exportTable({ rows, groups: meta, totals, total, groupBy, columnKeys, totalsMode, totalKeys = null }) {
  const columns = columnsFor(columnKeys);
  const showItemCodeColumn = columns.some(c => c.key === 'item_code');
  const shownKeys = totalKeys
    ? totalKeys.filter(k => columns.some(c => c.key === k && isTotalColumn(c)))
    : columns.filter(isTotalColumn).map(c => c.key);
  const hasFigures = (row) => Object.keys(row).length > 0;
  const grouping = groupingOf(groupBy);

  const out = groupRows(rows, meta).map(group => {
    const head = groupHead(group, grouping);
    const subtotals = showsGroupTotals(totalsMode) && group.ends
      ? totalsRowText(group.totals || {}, columns, shownKeys)
      : null;
    return {
      ...head,
      label: group.continued ? `${head.label || ''} (continued)`.trim() : (head.label || ''),
      meta: linesText(group.lineCount),
      rows: group.rows.map(row => Object.fromEntries(
        columns.map(col => [col.key, cellText(col, row, { showItemCodeColumn })])
      )),
      subtotals: subtotals && hasFigures(subtotals) ? subtotals : null,
      subtotalLabel: subtotalLabel(group, grouping)
    };
  });

  const bottom = showsBottomTotals(totalsMode) ? totalsRowText(totals || {}, columns, shownKeys) : null;
  return {
    columns: columns.map(c => ({ key: c.key, label: c.label, numeric: c.align === 'right' })),
    groups: out,
    totals: bottom && hasFigures(bottom) ? [bottom] : [],
    totalsLabel: bottom && hasFigures(bottom) ? grandTotalLabel(total) : ''
  };
}
